'use strict';

/**
 * SuperLmsSettingsController
 *
 * Super-admin "LMS Settings" page (WEB-3553). A super user (gated by the
 * SS_ORG_2FA_ENFORCEMENT permission) selects a law firm and manages per-law-firm
 * configuration. The law firm list comes from the super-only GET sec/super/law-firms
 * endpoint ({id, name, is_twofa_enforced}), so selecting a law firm needs no extra request.
 *
 * Sections:
 *   1. Security — "Enforce 2FA at login for all users" (`is_twofa_enforced` on the law firm).
 *      Per-section view/edit mode like the Developer Settings page. Persisted via
 *      POST sec/super/law-firms/{id}/set-2fa-enforcement {enabled}.
 *
 * The law firm dropdown is a searchable jQuery Nice Select widget (class "dashboard-select",
 * initialised in the blade); it is rebuilt via niceSelect('update') once the async law firm
 * list resolves so the late-populated ng-options render.
 */
class SuperLmsSettingsController {
    /**
     * @param {Object}   SSUtilService
     * @param {Object}   SSUserService  - permission/role helper (isSuper, hasPermission)
     * @param {Object}   SSHttpService  - API request helper
     * @param {Object}   OTPService     - 2FA gate for sensitive saves
     * @param {Object}   SSAlertService - user-facing alerts
     * @param {Function} $timeout       - Angular $timeout (defer nice-select refresh past digest)
     */
    constructor(SSUtilService, SSUserService, SSHttpService, OTPService, SSAlertService, $timeout) {
        const self = this;

        self.SSUtilService = SSUtilService;
        self.SSUserService = SSUserService;
        self.SSHttpService = SSHttpService;
        self.OTPService = OTPService;
        self.SSAlertService = SSAlertService;
        self.$timeout = $timeout;

        self.data = SHOWSUITE.data;

        self.lawFirms = null;
        self.selectedLawFirm = null;  // selected law firm id (ng-model of the dropdown)

        // Section 1 — Security (WEB-3553): org-level enforced 2FA at login
        self.twofaEnforced = false;       // toggle model, mirrors law_firm.is_twofa_enforced
        self.securityEditMode = false;    // false = read-only, true = editing
        self._securitySnapshot = false;   // value captured on entering edit (for Cancel)

        if (self.SSUserService.isSuper()) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.getLawFirms();
    }

    /**
     * Whether the current user may manage org-level 2FA enforcement.
     * @returns {boolean}
     */
    canManageOrg2FA() {
        return this.SSUserService.hasPermission('SS_ORG_2FA_ENFORCEMENT');
    }

    /**
     * Load the law firm dropdown options, then rebuild the searchable nice-select widget.
     * The endpoint itself requires SS_ORG_2FA_ENFORCEMENT, so skip the call (and the
     * guaranteed error alert) for users without it.
     */
    getLawFirms() {
        const self = this;

        if (!self.canManageOrg2FA()) {
            return;
        }

        loading(true);
        self.SSHttpService.getAPIRequest('sec/super/law-firms').then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            self.lawFirms = (response.data.law_firms || []).map(function (lawFirm) {
                lawFirm.is_twofa_enforced = parseInt(lawFirm.is_twofa_enforced || 0, 10);
                return lawFirm;
            });
            // ng-options populate after this digest; rebuild the nice-select so the options render
            self.$timeout(function () {
                jQuery('#law-firm-select').niceSelect('update');
            });
        });
    }

    /**
     * ng-change handler for the law firm dropdown. Resets the section to read-only and hydrates
     * the Security toggle from the already-loaded law firm list (no extra request needed).
     */
    lawFirmSelected() {
        const self = this;

        self.securityEditMode = false;
        let lawFirm = (self.lawFirms || []).find(function (lf) {
            return lf.id == self.selectedLawFirm;
        });
        self.twofaEnforced = !!(lawFirm && lawFirm.is_twofa_enforced);
    }

    // ---- Section 1: Security (WEB-3553) ----

    /** Enter edit mode for the Security section (snapshots current value for Cancel). */
    enableSecurityEdit() {
        const self = this;
        if (!self.canManageOrg2FA()) {
            return;
        }
        self._securitySnapshot = self.twofaEnforced;
        self.securityEditMode = true;
    }

    /** Discard the unsaved Security change and return to read-only. */
    cancelSecurityEdit() {
        const self = this;
        self.twofaEnforced = self._securitySnapshot;
        self.securityEditMode = false;
    }

    /** Persist the Security section. Gated by SS_ORG_2FA_ENFORCEMENT + 2FA. */
    saveSecuritySettings() {
        const self = this;
        if (!self.selectedLawFirm || !self.canManageOrg2FA() || !self.securityEditMode) {
            return;
        }
        self.OTPService.getVerified(self.OTPService.TYPE_SESSION_2FA, function () {
            self._saveSecuritySettings();
        });
    }

    /**
     * POST the enforcement flag for the selected law firm, then sync the local models
     * (toggle + cached law firm list row) from the server-confirmed value.
     */
    _saveSecuritySettings() {
        const self = this;
        let url = 'sec/super/law-firms/' + self.selectedLawFirm + '/set-2fa-enforcement';

        loading(true);
        self.SSHttpService.postAPIRequest(url, {enabled: !!self.twofaEnforced}).then(function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            let enforced = !!(response.data && parseInt(response.data.is_twofa_enforced || 0, 10));
            self.twofaEnforced = enforced;
            let lawFirm = (self.lawFirms || []).find(function (lf) {
                return lf.id == self.selectedLawFirm;
            });
            if (lawFirm) {
                lawFirm.is_twofa_enforced = enforced ? 1 : 0;
            }

            self.securityEditMode = false;
            self.SSAlertService.success('Security settings have been saved successfully.');
        });
    }

}

SuperLmsSettingsController.$inject = ['SSUtilService', 'SSUserService', 'SSHttpService', 'OTPService', 'SSAlertService', '$timeout'];
app.controller('SuperLmsSettingsController', SuperLmsSettingsController);
